import React, { ComponentProps, ComponentType, useCallback, useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import styled, { CSSProperties } from 'styled-components';
import { BreakpointProp } from '../../utils/styles/breakpoints';
import { useIsClickStartedFromRef } from '../hooks/useIsClickStartedFromRef';
import { IconClose } from '../icon/IconClose';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { Flex } from '../layout/Flex';
import { TransitionZoomIn } from '../transition/TransitionZoomIn';
import { TransitionProps } from '../transition/makeTransitionComponent';
import { Backdrop } from './Backdrop';

export interface DialogTitleProps extends ComponentProps<typeof Flex> {
    onClose: VoidFunction;
    hideCloseButton?: boolean;
    withBorderBottom?: boolean;
    closeRightOffset?: BreakpointProp<CSSProperties['right']>;
}

export const DialogTitle = (props: DialogTitleProps) => {
    const {
        children,
        withBorderBottom = true,
        hideCloseButton,
        closeRightOffset = 1,
        onClose,
        ...other
    } = props;

    return (
        <Flex
            px={{ xs: 2, sm: 3 }}
            py={2}
            overflow="hidden"
            width="100%"
            alignItems="center"
            direction="row"
            position="relative"
            background="var(--background-primary)"
            borderBottom={withBorderBottom ? '1px solid var(--divider)' : ''}
            {...other}
        >
            {children}
            {!hideCloseButton ? (
                <Box
                    position="absolute"
                    right={closeRightOffset}
                    top="50%"
                    transform="translateY(-50%)"
                >
                    <IconButton onClick={onClose} dataTestId={DataTestId.DIALOG_CLOSE_BUTTON}>
                        <IconClose />
                    </IconButton>
                </Box>
            ) : null}
        </Flex>
    );
};

export interface DialogFooterProps extends ComponentProps<typeof Flex> {
    withBorderTop?: boolean;
}

export const DialogFooter = (props: DialogFooterProps) => {
    const { children, withBorderTop = true, ...other } = props;

    return (
        <Flex
            px={{ xs: 2, sm: 3 }}
            py={2}
            overflow="hidden"
            width="100%"
            alignItems="center"
            direction="row"
            position="relative"
            background="var(--background-primary)"
            borderTop={withBorderTop ? '1px solid var(--divider)' : ''}
            {...other}
        >
            {children}
        </Flex>
    );
};

export const DialogContent = styled(Flex)
    .withConfig({ displayName: 'DialogContent' })
    .attrs((props) => ({
        py: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
        overflowY: 'auto',
        width: '100%',
        height: { xs: '100%', sm: 'auto' },
        background: 'var(--background-primary)',
        ...props,
    }))``;

export const DialogActions = styled(Flex)
    .withConfig({ displayName: 'DialogActions' })
    .attrs((props) => ({
        px: { xs: 2, sm: 3 },
        pb: { xs: 2, sm: 3 },
        spacing: { xs: 2, sm: 3 },
        width: '100%',
        background: 'var(--background-primary)',
        ...props,
        direction: 'row',
    }))``;

interface DialogProps extends ComponentProps<typeof Box> {
    TransitionComponent?: ComponentType<TransitionProps>;
    onClose?: VoidFunction;
    forwardRef?: React.Ref<HTMLDivElement>;
    preventScroll?: boolean;
}

export const Dialog = (props: DialogProps) => {
    const {
        children,
        onClose: onCloseBase = noop,
        TransitionComponent = TransitionZoomIn,
        forwardRef,
        preventScroll = true,
        ...other
    } = props;

    const dialogRef = useRef<HTMLDivElement>(null);
    const clickStartedFromDialog = useIsClickStartedFromRef(dialogRef);

    const onClose = useCallback(() => {
        if (clickStartedFromDialog) {
            return;
        }

        onCloseBase();
    }, [clickStartedFromDialog, onCloseBase]);

    useEffect(() => {
        if (!preventScroll) {
            return;
        }

        window.document.body.style.setProperty('overflow', 'hidden');

        return () => {
            window.document.body.style.removeProperty('overflow');
        };
    }, [preventScroll]);

    return (
        <Backdrop ref={forwardRef} onClose={onClose}>
            <TransitionComponent>
                <Flex
                    background="var(--background-primary)"
                    ref={dialogRef}
                    direction="column"
                    overflow="hidden"
                    width="100%"
                    height={{ xs: '100%', sm: 'auto !important' }}
                    maxWidth={{ sm: 580 }}
                    maxHeight={{ sm: '90vh' }}
                    borderRadius={{ sm: 3 }}
                    {...other}
                >
                    {children}
                </Flex>
            </TransitionComponent>
        </Backdrop>
    );
};
